import React from 'react';
import {CardNav} from './CardNavigation';
import {CardActions, CardBody, CardHeader} from './CardSections';
import {CardWrapper} from './CardWrapper';

/**
 * The Card UI component is a user interface element crafted to efficiently present information.
 * It features a rectangular container that emulates the appearance of a physical card,
 * allowing seamless integration of various content types, including text, images, buttons, and interactive elements.
 *
 * @param {object} param0
 *
 * @param {import('react').ReactNode} param0.children
 *
 * @param {'sm'} [param0.size]
 * CSS variable names for adjusting the size of the card you're using:
 *   - 'sm': Smaller variant of the card.
 *
 * **Internal Child component**
 *
 * Both `<Card.Wrapper>` and `<Card.Body>` are internal child styled components
 * included with the Card component. This means you won't need to include them
 * explicitly when using the Card component. However, if you want to customize aspects
 * like background color, margin, text size, or any other specific styling within a plugin
 * or for a particular purpose, you can access these styled components.
 *
 * Let's assume you have wrapped  a custom styled component named 'Wrapper' around the Card .
 * You can modify them as follows:
 *
 *  @example
 * ```jsx
 * const Wrapper = styled.div`
 *   ${Card.Wrapper} {
 *       margin-bottom: var(--space-xl);
 *   }
 *   ${Card.Body} {
 *       padding-bottom: var(--space-xl);
 *   }
 * `;
 * ```
 */
const Card = ({children, size}) => (
    <Card.Wrapper size={size}>
        <Card.Body>{children}</Card.Body>
    </Card.Wrapper>
);

Card.Wrapper = CardWrapper;
Card.Body = CardBody;

/**
 * It's essential to always keep the Card.Header as the first child of the Card.
 * When you use the `<Card.Header>` component within the `<Card>` component, it creates a gray-colored header at the top section of the card, positioned above the card body.
 * The primary purpose of `<Card.Header>` is to serve as a title for the card, providing a uniform appearance and conveying importance.
 * While not always necessary, its inclusion maintains consistency and emphasizes the significance of the card.
 * There are instances where we choose not to use` <Card.Header>`, such as when displaying cards in a list row within a sidebar. This decision prioritizes simplicity, particularly in scenarios with extensive content on the page.
 *
 * @example
 * ```jsx
 *  <Card>
 *     <Card.Header>Text</Card.Header> // <-- Always as the first child
 *      Rest of card body content
 *  </Card>
 * ```
 */
Card.Header = CardHeader;

/**
 * To position buttons in the corner of a `<Card>` component, utilize `<Card.Actions>`.
 * Insert this component directly within the `<Card>`. It's essential to use the `<Btn>`
 * component with the `round` prop to achieve a compact and appropriate design that
 * complements the card's appearance. Using any other button may result in a clunky
 * and unattractive look.
 *
 * To ensure the buttons are visible only when hovering over the card,
 * apply the hover (boolean) prop to `<Card.Actions>`. This will not only
 * hide the buttons until needed but also provide a highlight color to the
 * card when hovered over, enhancing user interaction and feedback.
 *
 * @param {object} param0
 *
 * @param {boolean} [param0.hover]
 * When you add the hover prop to `<Card.Actions>`, it hides the action buttons by default,
 * showing them only when you hover over the card. This is handy when you have lots of cards in a long list.
 *
 * In such cases, showing all action buttons all the time can be overwhelming.
 * With the hover effect, the focus stays on the card's main content until needed,
 * making it easier for users to find what they're looking for without cluttering the interface.
 *
 * Plus, when you hover over the card, it gets highlighted. This serves two purposes:
 * it tells users that actions are available and helps them stay focused in a busy interface,
 * especially when there are lots of cards stacked vertically. This way, users can quickly
 * see which card they're working with, making the interface easier to use.
 *
 * @example
 * ```jsx
 * <Card>
 *     <Card.Actions hover> // <-- hover props to the Card.Actions is optional
 *         <Btn round color='danger'/>  // <-- Btn component with the round props!!
 *     </Card.Actions>
 * </Card>
 * ```
 */
Card.Actions = CardActions;

/**
 * The `<Card.Nav>` component allows you to use a card as a navigation link.
 *
 * @param {object} param0
 * Props.
 *
 * @param {string | {pathname: string; search: string}} param0.to
 * The path to navigate to when the card is clicked.
 *
 * @param {'sm'} param0.size
 * Specify the size of the component.
 */
Card.Nav = CardNav;

export {Card, CardWrapper};
